import { AxiosError } from 'axios';
import type { AppError } from '../types/common';

interface ErrorResponseData {
  message?: string;
}

export function normalizeError(error: unknown): AppError {
  if (error instanceof AxiosError) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return {
        code: 'TIMEOUT',
        message: 'Request timeout - please try again',
        details: `Request exceeded timeout limit`,
      };
    }

    if (!error.response) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error - please check your connection',
        details: error.message,
      };
    }

    const status = error.response.status;
    const data = error.response.data as ErrorResponseData | undefined;

    if (status >= 500) {
      return {
        code: 'SERVER_ERROR',
        message: data?.message || 'Server error - please try again later',
        details: `HTTP ${status}: ${error.message}`,
      };
    }

    if (status === 401) {
      return {
        code: 'UNAUTHORIZED',
        message: 'Authentication failed - invalid token',
        details: data?.message || error.message,
      };
    }

    if (status === 403) {
      return {
        code: 'FORBIDDEN',
        message: 'Access denied - insufficient permissions',
        details: data?.message || error.message,
      };
    }

    if (status === 404) {
      return {
        code: 'NOT_FOUND',
        message: 'Resource not found',
        details: data?.message || error.message,
      };
    }

    if (status >= 400) {
      return {
        code: 'CLIENT_ERROR',
        message: data?.message || 'Invalid request',
        details: `HTTP ${status}: ${error.message}`,
      };
    }
  }

  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
      details: error.stack,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
    details: String(error),
  };
}
